import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, FileUp } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import {
  CSV_FIELD_LABELS,
  DATE_FORMATS,
  autoMapStatement,
  bankingKey,
  guessDateFormat,
  mappedRows,
  useBankAccounts,
  type BankAccount,
  type CsvField,
  type CsvOptions,
  type DateFormat,
  type ImportResult,
} from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { parseCsv, type ParsedCsv } from '@/lib/imports';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/banking/import')({
  component: ImportStatementPage,
  validateSearch: (s: Record<string, unknown>): { accountId?: string } =>
    typeof s['accountId'] === 'string' ? { accountId: s['accountId'] } : {},
});

type Step = 'upload' | 'map' | 'review' | 'done';
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'upload', label: 'Account and file' },
  { key: 'map', label: 'Map columns' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];
const MAX_ROWS = 5000;

type Source =
  | { format: 'csv'; name: string; csv: ParsedCsv }
  | { format: 'ofx'; name: string; content: string };

/** Screen B2 (document 06): Import statement (CSV or OFX). */
function ImportStatementPage() {
  const orgId = useOrgId();
  const me = useMe();
  const banks = useBankAccounts();
  const { accountId } = Route.useSearch();
  return (
    <>
      <PageHeader
        title="Import bank statement"
        description="Upload a CSV or OFX file from your bank. Transactions go to Bank transactions › For review."
        actions={
          <Button variant="outline" asChild>
            <Link to="/banking" search={accountId ? { accountId } : {}}>
              Back to bank transactions
            </Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState
          title="Only accounting staff can import statements"
          hint="Send the statement to your accountant."
        />
      ) : banks.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : banks.isError ? (
        <EmptyState title="Could not load bank accounts" hint={banks.error.message} />
      ) : banks.data.length === 0 ? (
        <EmptyState
          title="No bank accounts yet"
          hint="Add a Bank or Credit card account to the chart of accounts first."
        />
      ) : (
        <Wizard key={orgId} banks={banks.data} initialAccountId={accountId ?? null} />
      )}
    </>
  );
}

function Wizard({
  banks,
  initialAccountId,
}: {
  banks: BankAccount[];
  initialAccountId: string | null;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('upload');
  const [accountId, setAccountId] = useState(
    banks.find((b) => b.accountId === initialAccountId)?.accountId ?? banks[0]!.accountId,
  );
  const bank = banks.find((b) => b.accountId === accountId)!;
  const [source, setSource] = useState<Source | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<CsvField, string>>>({});
  const [options, setOptions] = useState<CsvOptions>({
    dateFormat: 'dd/MM/yyyy',
    amountMode: 'single',
    positive: 'money_in',
  });
  const [onlyProblems, setOnlyProblems] = useState(false);

  const payload = () =>
    source?.format === 'ofx'
      ? { format: 'ofx', accountId, fileName: source.name, content: source.content }
      : {
          format: 'csv',
          accountId,
          fileName: source!.name,
          rows: mappedRows((source as { csv: ParsedCsv }).csv, mapping, options.amountMode),
          options,
          mapping,
        };

  const preview = useMutation({
    mutationFn: (p: object) =>
      api<ImportResult>(`/orgs/${orgId}/banking/imports/preview`, {
        method: 'POST',
        body: JSON.stringify(p),
      }),
    onSuccess: () => {
      setOnlyProblems(false);
      setStep('review');
    },
  });
  const run = useMutation({
    mutationFn: () =>
      api<ImportResult>(`/orgs/${orgId}/banking/imports`, {
        method: 'POST',
        body: JSON.stringify(payload()),
      }),
    onSuccess: async () => {
      setStep('done');
      await Promise.all([
        qc.invalidateQueries({ queryKey: bankingKey(orgId) }),
        qc.invalidateQueries({ queryKey: ['accounts', orgId] }),
      ]);
    },
  });

  async function choose(f: File | undefined) {
    setFileError(null);
    preview.reset();
    if (!f) return;
    try {
      const text = await f.text();
      if (/\.(ofx|qfx)$/i.test(f.name) || /<OFX>/i.test(text.slice(0, 4000))) {
        const s: Source = { format: 'ofx', name: f.name, content: text };
        setSource(s);
        preview.mutate({ format: 'ofx', accountId, fileName: f.name, content: text });
        return;
      }
      if (!/\.(csv|txt)$/i.test(f.name) && f.type !== 'text/csv')
        throw new Error('Choose a .csv or .ofx file. In Excel, use File › Save as › CSV UTF-8.');
      const csv = await parseCsv(f);
      if (!csv.rows.length) throw new Error('The file has a header row but no transactions.');
      if (csv.rows.length > MAX_ROWS)
        throw new Error(
          `The file has ${csv.rows.length.toLocaleString('en-GB')} rows; import at most ${MAX_ROWS.toLocaleString('en-GB')} at a time.`,
        );
      const auto = autoMapStatement(csv.headers, bank.importMapping);
      const dateCol = auto.mapping.date ? csv.headers.indexOf(auto.mapping.date) : -1;
      const guessed = dateCol >= 0 ? guessDateFormat(csv.rows.map((r) => r[dateCol] ?? '')) : null;
      setSource({ format: 'csv', name: f.name, csv });
      setMapping(auto.mapping);
      setOptions({
        dateFormat: bank.importMapping?.options.dateFormat ?? guessed ?? 'dd/MM/yyyy',
        amountMode: auto.amountMode,
        positive:
          bank.importMapping?.options.positive ??
          (bank.accountType === 'credit_card' ? 'money_out' : 'money_in'),
      });
      setStep('map');
    } catch (e) {
      setFileError((e as Error).message);
    }
  }

  function reset() {
    setSource(null);
    setMapping({});
    preview.reset();
    run.reset();
    setStep('upload');
  }

  const result = step === 'done' ? run.data : preview.data;

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <Stepper step={step} skipMap={source?.format === 'ofx'} />
      {step === 'upload' ? (
        <>
          <ErrorBanner>{fileError ?? preview.error?.message ?? null}</ErrorBanner>
          <div className="max-w-md">
            <Field label="Bank account">
              {(id) => (
                <NativeSelect
                  id={id}
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  {banks.map((b) => (
                    <option key={b.accountId} value={b.accountId}>
                      {b.code} · {b.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <DropZone busy={preview.isPending} onFile={choose} />
        </>
      ) : null}
      {step === 'map' && source?.format === 'csv' ? (
        <MapStep
          csv={source.csv}
          name={source.name}
          bank={bank}
          mapping={mapping}
          setMapping={setMapping}
          options={options}
          setOptions={setOptions}
          error={preview.error?.message ?? null}
          busy={preview.isPending}
          onBack={reset}
          onNext={() => preview.mutate(payload())}
        />
      ) : null}
      {(step === 'review' || step === 'done') && result ? (
        <ReviewStep
          result={result}
          bank={bank}
          done={step === 'done'}
          onlyProblems={onlyProblems}
          setOnlyProblems={setOnlyProblems}
          error={run.error?.message ?? null}
          busy={run.isPending}
          onBack={() => (source?.format === 'csv' ? setStep('map') : reset())}
          onImport={() => run.mutate()}
          onAnother={reset}
        />
      ) : null}
    </div>
  );
}

function Stepper({ step, skipMap }: { step: Step; skipMap: boolean }) {
  const steps = skipMap ? STEPS.filter((s) => s.key !== 'map') : STEPS;
  const at = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm" aria-label="Import steps">
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2">
          <span
            className={cn(
              'flex size-6 items-center justify-center rounded-full border text-xs',
              i < at && 'border-primary bg-primary text-primary-foreground',
              i === at && 'border-primary text-primary',
              i > at && 'text-muted-foreground',
            )}
          >
            {i < at ? <Check className="size-3.5" /> : i + 1}
          </span>
          <span
            className={cn(i === at ? 'font-medium' : 'text-muted-foreground')}
            aria-current={i === at ? 'step' : undefined}
          >
            {s.label}
          </span>
          {i < steps.length - 1 ? <span className="mx-1 h-px w-8 bg-border" /> : null}
        </li>
      ))}
    </ol>
  );
}

function DropZone({ busy, onFile }: { busy: boolean; onFile: (f: File | undefined) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    onFile(e.dataTransfer.files[0]);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed bg-card px-6 py-10 text-center',
        over && 'border-primary bg-primary/5',
      )}
    >
      <FileUp className="size-8 text-primary" />
      <div>
        <p className="font-medium">Drag and drop a statement file here, or browse</p>
        <p className="text-sm text-muted-foreground">
          CSV (any bank: you match the columns once, then we remember them for this account) or OFX
          / QFX. Up to {MAX_ROWS.toLocaleString('en-GB')} transactions per file. Transactions
          already imported are skipped.
        </p>
      </div>
      <Button disabled={busy} onClick={() => input.current?.click()}>
        {busy ? 'Reading…' : 'Browse'}
      </Button>
      <input
        ref={input}
        type="file"
        accept=".csv,.txt,.ofx,.qfx,text/csv"
        className="sr-only"
        aria-label="Statement file"
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function MapStep({
  csv,
  name,
  bank,
  mapping,
  setMapping,
  options,
  setOptions,
  error,
  busy,
  onBack,
  onNext,
}: {
  csv: ParsedCsv;
  name: string;
  bank: BankAccount;
  mapping: Partial<Record<CsvField, string>>;
  setMapping: (m: Partial<Record<CsvField, string>>) => void;
  options: CsvOptions;
  setOptions: (o: CsvOptions) => void;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const [tried, setTried] = useState(false);
  const fields: Array<{ key: CsvField; required?: boolean; hint?: string }> = [
    { key: 'date', required: true },
    { key: 'description', required: true, hint: 'Narrative or details. Payee is used when blank.' },
    { key: 'payee' },
    { key: 'reference', hint: 'Cheque or transaction reference.' },
    ...(options.amountMode === 'single'
      ? [{ key: 'amount' as const, required: true }]
      : [
          { key: 'moneyIn' as const, hint: 'Credits / deposits.' },
          { key: 'moneyOut' as const, hint: 'Debits / withdrawals.' },
        ]),
    { key: 'balance', hint: 'Running balance; the latest one becomes the bank balance.' },
  ];
  const first = csv.rows[0] ?? [];
  const used = fields.map((f) => mapping[f.key]).filter(Boolean) as string[];
  const twice = new Set(used.filter((h, i) => used.indexOf(h) !== i));
  const missing = fields.filter((f) => f.required && !mapping[f.key]);
  const splitMissing = options.amountMode === 'split' && !mapping.moneyIn && !mapping.moneyOut;
  const blocked = missing.length > 0 || twice.size > 0 || splitMissing;
  const set = (key: CsvField, header: string) => {
    const next = { ...mapping };
    if (header) next[key] = header;
    else delete next[key];
    setMapping(next);
  };
  return (
    <>
      <ErrorBanner>
        {error ??
          (tried && missing.length
            ? `Map a column to ${missing.map((f) => CSV_FIELD_LABELS[f.key]).join(', ')}.`
            : tried && splitMissing
              ? 'Map the money in or money out column.'
              : tried && twice.size
                ? `Each column can feed one field: ${[...twice].join(', ')} is mapped twice.`
                : null)}
      </ErrorBanner>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{name}</span> ·{' '}
        {csv.rows.length.toLocaleString('en-GB')} rows into{' '}
        <span className="font-medium text-foreground">{bank.name}</span>.{' '}
        {bank.importMapping
          ? 'We used the mapping saved for this account; '
          : 'We matched what we recognised; '}
        check the rest. The mapping is remembered for this account.
      </p>
      <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 md:grid-cols-3">
        <Field label="Date format">
          {(id) => (
            <NativeSelect
              id={id}
              value={options.dateFormat}
              onChange={(e) => setOptions({ ...options, dateFormat: e.target.value as DateFormat })}
            >
              {DATE_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label="Amounts are in">
          {(id) => (
            <NativeSelect
              id={id}
              value={options.amountMode}
              onChange={(e) =>
                setOptions({ ...options, amountMode: e.target.value as CsvOptions['amountMode'] })
              }
            >
              <option value="single">One column (positive and negative)</option>
              <option value="split">Two columns (money in, money out)</option>
            </NativeSelect>
          )}
        </Field>
        {options.amountMode === 'single' ? (
          <Field label="Positive amounts are">
            {(id) => (
              <NativeSelect
                id={id}
                value={options.positive}
                onChange={(e) =>
                  setOptions({ ...options, positive: e.target.value as CsvOptions['positive'] })
                }
              >
                <option value="money_in">Money in (most bank accounts)</option>
                <option value="money_out">Money out (most credit cards)</option>
              </NativeSelect>
            )}
          </Field>
        ) : null}
      </div>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[240px]">Yedo Books field</TableHead>
              <TableHead className="w-[280px]">Column in your file</TableHead>
              <TableHead>First row</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((f) => {
              const header = mapping[f.key] ?? '';
              const i = csv.headers.indexOf(header);
              const bad = (tried && f.required && !header) || (header && twice.has(header));
              return (
                <TableRow key={f.key}>
                  <TableCell>
                    <div className="font-medium">
                      {CSV_FIELD_LABELS[f.key]}
                      {f.required ? ' *' : ''}
                    </div>
                    {f.hint ? <div className="text-xs text-muted-foreground">{f.hint}</div> : null}
                  </TableCell>
                  <TableCell>
                    <NativeSelect
                      aria-label={`Column for ${CSV_FIELD_LABELS[f.key]}`}
                      aria-invalid={Boolean(bad)}
                      value={header}
                      onChange={(e) => set(f.key, e.target.value)}
                    >
                      <option value="">Not mapped</option>
                      {csv.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </NativeSelect>
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate text-sm text-muted-foreground">
                    {i >= 0 ? (first[i] ?? '') : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            setTried(true);
            if (!blocked) onNext();
          }}
        >
          {busy ? 'Checking transactions…' : 'Next'}
        </Button>
      </div>
    </>
  );
}

function ReviewStep({
  result,
  bank,
  done,
  onlyProblems,
  setOnlyProblems,
  error,
  busy,
  onBack,
  onImport,
  onAnother,
}: {
  result: ImportResult;
  bank: BankAccount;
  done: boolean;
  onlyProblems: boolean;
  setOnlyProblems: (v: boolean) => void;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onImport: () => void;
  onAnother: () => void;
}) {
  const rows = onlyProblems ? result.rows.filter((r) => r.status !== 'ok') : result.rows;
  const n = (v: number) => v.toLocaleString('en-GB');
  return (
    <>
      <ErrorBanner>{error}</ErrorBanner>
      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-sm',
          done ? 'border-primary/30 bg-primary/5' : 'bg-card',
        )}
        role="status"
      >
        {done ? (
          <>
            <span className="font-medium">
              Imported {n(result.imported ?? 0)} transactions into {bank.name}.
            </span>{' '}
            {result.autoAdded ? `${n(result.autoAdded)} were added automatically by rules. ` : ''}
            {result.ruleSuggested ? `${n(result.ruleSuggested)} have a rule suggestion. ` : ''}
            {result.duplicates ? `${n(result.duplicates)} duplicates were skipped. ` : ''}
            {result.errors ? `${n(result.errors)} rows with errors were skipped.` : ''}
          </>
        ) : (
          <>
            <span className="font-medium">
              {n(result.ok)} of {n(result.total)} transactions are ready to import.
            </span>{' '}
            {result.duplicates
              ? `${n(result.duplicates)} are already in Yedo Books and will be skipped. `
              : ''}
            {result.errors ? `${n(result.errors)} rows have errors and will be skipped.` : ''}
          </>
        )}
        {result.statementBalance ? (
          <div className="mt-1 text-muted-foreground">
            Statement balance {bank.currency} {formatAmount(result.statementBalance.amount)} on{' '}
            {displayDate(result.statementBalance.date)}.
          </div>
        ) : null}
      </div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={onlyProblems}
          onChange={(e) => setOnlyProblems(e.target.checked)}
        />
        Show only duplicates and errors
      </label>
      <div className="max-h-[480px] overflow-y-auto rounded-lg border bg-card">
        <Table>
          <TableHeader className="sticky top-0 bg-card">
            <TableRow>
              <TableHead className="w-[70px] text-right">Row</TableHead>
              <TableHead className="w-[110px]">Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[140px] text-right">Amount</TableHead>
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead>Errors</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.row}>
                <TableCell className="tnum text-right text-muted-foreground">{r.row}</TableCell>
                <TableCell className="tnum">{r.date ? displayDate(r.date) : ''}</TableCell>
                <TableCell>{r.description ?? ''}</TableCell>
                <TableCell className="tnum text-right">{formatAmount(r.amount)}</TableCell>
                <TableCell>
                  {r.status === 'ok' ? (
                    <Badge variant="secondary">{done ? 'Imported' : 'Ready'}</Badge>
                  ) : r.status === 'duplicate' ? (
                    <Badge variant="outline">Duplicate</Badge>
                  ) : (
                    <Badge variant="destructive">Error</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {r.errors.map((e, i) => (
                    <div key={i} className="text-destructive">
                      <span className="font-medium">
                        {CSV_FIELD_LABELS[e.field as CsvField] ?? 'Transaction'}:{' '}
                      </span>
                      {e.message}
                    </div>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        {done ? (
          <>
            <Button variant="outline" onClick={onAnother}>
              Import another file
            </Button>
            <Button asChild>
              <Link to="/banking" search={{ accountId: bank.accountId, tab: 'for_review' }}>
                Review transactions
              </Link>
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button disabled={busy || result.ok === 0} onClick={onImport}>
              {busy
                ? 'Importing…'
                : `Import ${n(result.ok)} ${result.ok === 1 ? 'transaction' : 'transactions'}`}
            </Button>
          </>
        )}
      </div>
    </>
  );
}
